import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Typography } from '../../Typography';
import { TruncatedText } from '../../TruncatedText';
import InputText from '../../../inputs/InputText';
import { normalizeDecimalInput } from '../../../../utils/decimalFormat';
import { useAuth } from '../../../../hooks/useAuth';
import { Role } from '../../../../types/auth';
import {
  TableContainer,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  CategoryIndicator,
  ActionCell,
  RemoveIcon,
} from './styles';

// Icons
import RemoveIconSvg from '../../../../assets/icons/remove-file-icon.svg';
import { ActionBudgetItemsTableProps, ActionBudgetItemRow } from './types';

const columnHelper = createColumnHelper<ActionBudgetItemRow>();

/**
 * Celda editable de cantidad con estado de TEXTO local.
 *
 * El input no puede ser un controlado puro contra el número del padre: estados
 * intermedios como '' (borrado), '1.' o '1,' no son números válidos, así que no
 * se confirmarían y un `value={padre}` los revertiría → imposible reescribir.
 * Aquí el texto local es la fuente de verdad mientras se edita (permite coma,
 * punto y borrado), y solo se confirma al padre cuando hay un número > 0.
 */
const EditableQuantityCell: React.FC<{
  initialValue: number;
  onCommit: (value: number) => void;
}> = ({ initialValue, onCommit }) => {
  const [text, setText] = useState<string>(() =>
    initialValue ? String(initialValue) : ''
  );

  // Se confirma al padre SOLO al salir del campo (blur/Enter), no en cada tecla:
  // confirmar en cada pulsación re-renderiza el padre y el input pierde el foco.
  const commit = () => {
    const parsed = parseFloat(text);
    if (!isNaN(parsed) && parsed > 0) {
      onCommit(parsed);
    }
  };

  return (
    <InputText
      type="text"
      inputMode="decimal"
      pattern="^\d*([.,]\d{0,2})?$"
      value={text}
      onChange={(e) => setText(normalizeDecimalInput(e.target.value))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.currentTarget as HTMLInputElement).blur();
          return;
        }
        // Acepta coma/punto (separador decimal es-ES); bloquea letras/operadores.
        if (e.key.length === 1 && !/[\d.,]/.test(e.key)) {
          e.preventDefault();
        }
      }}
      inputSize="S"
      typeInput="normal"
      style={{ textAlign: 'right' }}
    />
  );
};

export const ActionBudgetItemsTable: React.FC<ActionBudgetItemsTableProps> = ({
  items,
  onRemove,
  onUpdateQuantity,
  showPrices = false,
  isReadOnly = false,
}) => {
  const { t } = useTranslation(['workOrders', 'common', 'tables']);
  const { user } = useAuth();

  // Guard: Tablets no deben ver esta tabla
  if (user?.user?.rolActive === Role.Tablet) {
    return null;
  }

  // Verificar si el usuario puede ver precios
  const canViewPrices = useMemo(() => {
    if (!user) return false;
    return (
      showPrices &&
      (user?.user?.rolActive === Role.SuperAdmin ||
        user?.user?.rolActive === Role.Coex)
    );
  }, [user, showPrices]);

  // Definir columnas
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      columnHelper.accessor('category', {
        id: 'category',
        header: t('workOrders:categoryCode'),
        cell: (info) => {
          const category = info.getValue();
          // Verificación de seguridad para evitar errores de undefined
          if (!category || typeof category !== 'object') {
            return (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <CategoryIndicator color="#cccccc" />
                <Typography variant="regular" size="S">
                  -
                </Typography>
              </div>
            );
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CategoryIndicator color={category.color || '#cccccc'} />
              <Typography variant="regular" size="S">
                {category.code || '-'}
              </Typography>
            </div>
          );
        },
        size: 120,
      }),
      columnHelper.accessor('code', {
        id: 'code',
        header: t('workOrders:budgetItemCode'),
        cell: (info) => (
          <Typography variant="medium" size="S">
            {info.getValue()}
          </Typography>
        ),
        size: 120,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: t('workOrders:budgetItemDescription'),
        cell: (info) => (
          <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <TruncatedText
              text={info.getValue()}
              maxLength={60}
              size="S"
              variant="regular"
            />
          </div>
        ),
        size: 300,
      }),
      columnHelper.accessor('unit', {
        id: 'unit',
        header: t('workOrders:unit'),
        cell: (info) => (
          <Typography variant="regular" size="S">
            {info.getValue()}
          </Typography>
        ),
        size: 80,
      }),
      columnHelper.accessor('quantity', {
        id: 'quantity',
        header: t('workOrders:quantity'),
        cell: (info) => {
          const quantity = info.getValue();
          const row = info.row.original;

          if (isReadOnly || !onUpdateQuantity) {
            return (
              <Typography variant="regular" size="S">
                {quantity.toLocaleString('es-ES', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            );
          }

          return (
            <EditableQuantityCell
              initialValue={quantity}
              onCommit={(value) => onUpdateQuantity(row.id, value)}
            />
          );
        },
        size: 100,
      }),
    ];

    // Añadir columnas de precios si el usuario puede verlas
    if (canViewPrices) {
      baseColumns.push(
        columnHelper.accessor('unitPrice', {
          id: 'unitPrice',
          header: t('workOrders:unitPrice'),
          cell: (info) => (
            <Typography variant="regular" size="S">
              {(info.getValue() as number).toLocaleString('es-ES', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              })}
            </Typography>
          ),
          size: 120,
        }),
        columnHelper.accessor('totalAmount', {
          id: 'totalAmount',
          header: t('workOrders:totalAmount'),
          cell: (info) => (
            <Typography variant="medium" size="S">
              {(info.getValue() as number).toLocaleString('es-ES', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          ),
          size: 120,
        })
      );
    }

    // Añadir columna de acciones si no es solo lectura
    if (!isReadOnly && onRemove) {
      baseColumns.push(
        columnHelper.display({
          id: 'actions',
          header: t('tables:headers.actions'),
          cell: (info) => (
            <ActionCell>
              <RemoveIcon
                src={RemoveIconSvg}
                alt={t('workOrders:removeBudgetItem')}
                onClick={() => onRemove(info.row.original.id)}
              />
            </ActionCell>
          ),
          size: 120,
        })
      );
    }

    return baseColumns;
  }, [t, canViewPrices, isReadOnly, onRemove, onUpdateQuantity]);

  // Configurar tabla
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                style={{ textAlign: 'center', padding: '2rem' }}
              >
                <Typography variant="regular" size="M" colorText="greyDark">
                  {t('workOrders:minimumOneItemRequired')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    data-description={cell.column.id === 'description'}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
